import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { Button } from "@moe/oss/ui/button";
import { LoadingSpinner } from "@moe/oss/ui/loading-spinner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@moe/oss/ui/tooltip";
import React from "react";

export type StatusType = "idle" | "pending" | "success" | "error";

interface StatusConfig {
  icon?: React.ReactNode;
  text: string;
  tooltip?: string;
}

interface StatusButtonProps {
  statusConfig: {
    [key in StatusType]: StatusConfig;
  };
  status: StatusType;
  className?: string;
  [key: string]: any;
}

export function StatusButton({ statusConfig, status, className = "", ...rest }: StatusButtonProps) {
  const config = statusConfig[status];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Button
            variant="status"
            className={`flex items-center space-x-2 disabled:bg-transparent ${getStatusStyles(status)} ${className}`}
            disabled={status === "pending"}
            {...rest}
          >
            {config.icon || getDefaultStatusIcon(status)}
            <span>{config.text}</span>
          </Button>
        </TooltipTrigger>

        {config.tooltip && (
          <TooltipContent>
            <p>{config.tooltip}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

function getStatusStyles(status: StatusType) {
  switch (status) {
    case "pending":
      return "cursor-wait hover:bg-transparent";
    case "idle":
      return "hover:text-tx-primary";
    case "success":
      return "text-tx-primary";
    case "error":
      return "text-tx-primary";
    default:
      return "";
  }
}

function getDefaultStatusIcon(status: StatusType) {
  switch (status) {
    case "pending":
      return <LoadingSpinner className="size-5" />;
    case "success":
      return <CheckCircleIcon className="size-5" />;
    case "error":
      return <ExclamationCircleIcon className="size-5" />;
    default:
      return null;
  }
}
